import { FITNESS_WORKOUT_FINISH_LIMITS as LIMITS } from '../../../supabase/functions/_shared/fitnessWorkoutFinishDomain';
import { freeWorkoutTimeDefault } from '@/lib/fitnessExerciseIdentity';
import type { FitnessWorkoutExercise } from '@/lib/fitnessWorkoutController';
import { copyFitnessExerciseMetadata, type FitnessExerciseMetadata, type FitnessSetType } from '../../../supabase/functions/_shared/fitnessWorkoutMetadata';
import { buildWorkoutSetSchedule } from './workoutSchedule';

const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
function text(value: unknown, maximum: number, fallback?: string): string {
  if ((value == null || value === '') && fallback !== undefined) return fallback;
  if (typeof value !== 'string' || !value.trim() || value.length > maximum) throw new Error('Invalid workout text');
  return value;
}
function number(value: unknown, fallback: number, maximum: number, integer = false, minimum = 0): number {
  if (value == null) return fallback;
  if (typeof value !== 'number' || !Number.isFinite(value) || value < minimum || value > maximum || integer && !Number.isInteger(value)) throw new Error('Invalid workout number');
  return value;
}
function rows(value: unknown, maximum: number): unknown[] {
  if (!Array.isArray(value) || value.length > maximum) throw new Error('Invalid workout list');
  return value;
}
export function readWorkoutSetTypes(value: unknown, count: number): FitnessSetType[] | undefined {
  if (value === undefined) return undefined;
  if (!Array.isArray(value) || value.length !== count || Reflect.ownKeys(value).length !== count + 1
    || Array.from({ length: count }, (_, index) => Object.getOwnPropertyDescriptor(value, String(index)))
      .some(descriptor => !descriptor || descriptor.enumerable !== true || !('value' in descriptor) || descriptor.value !== 'warmup' && descriptor.value !== 'working')) throw new Error('Invalid workout set types');
  return [...value];
}
function materialize(name: string, muscle: string, weight: number, amount: number, count: number, timed: boolean, gifUrl = '', restSeconds?: number, metadata: FitnessExerciseMetadata = {}, setTypes?: FitnessSetType[]): FitnessWorkoutExercise {
  return { name, muscle, gifUrl, defaultReps: amount, defaultWeight: weight, timed, ...metadata,
    ...(restSeconds === undefined ? {} : { restSeconds }),
    sets: Array.from({ length: count }, (_, index) => ({ weight, reps: amount, done: false, ...(setTypes ? { type: setTypes[index] } : {}) })),
  };
}

/** Validate browser route JSON before allocating sets or publishing runtime state. */
export function prepareFreeWorkoutExercises(value: unknown): FitnessWorkoutExercise[] {
  const exercises = rows(value, LIMITS.exercises).map(item => {
    if (!record(item)) throw new Error('Invalid free workout exercise');
    const name = text(item.name, LIMITS.name), muscle = text(item.muscle, LIMITS.muscle, '');
    const weight = number(item.defaultWeight, 0, LIMITS.weight);
    const count = number(item.sets, 3, LIMITS.sets, true, 1);
    const requested = number(item.defaultReps, 10, LIMITS.reps, true, 1);
    const duration = item.durationSeconds == null ? null : number(item.durationSeconds, 30, LIMITS.reps, true, 1);
    if (item.timed !== undefined && typeof item.timed !== 'boolean') throw new Error('Invalid workout unit');
    const timeDefault = freeWorkoutTimeDefault(name);
    const timed = timeDefault !== null || item.timed === true || duration !== null;
    const amount = duration ?? (item.timed === true ? requested : timeDefault ?? requested);
    const rest = item.restSeconds == null ? undefined : number(item.restSeconds, 60, LIMITS.rest_seconds);
    return materialize(name, muscle, weight, amount, count, timed, "", rest, copyFitnessExerciseMetadata(item), readWorkoutSetTypes(item.setTypes, count));
  });
  buildWorkoutSetSchedule(exercises);
  return exercises;
}

export interface WorkoutPlanExerciseInput extends FitnessExerciseMetadata {
  name: string; muscle: string; sets: number; amount: number; timed: boolean;
  weight: number; restSeconds: number; gifUrl: string; setTypes?: FitnessSetType[];
}
export interface WorkoutPlanDayInput { name: string; exercises: WorkoutPlanExerciseInput[] }

function planExercise(value: unknown): WorkoutPlanExerciseInput {
  if (!record(value)) throw new Error('Invalid planned exercise');
  const name = text(value.name, LIMITS.name);
  const muscle = text(value.muscle_group ?? value.muscle, LIMITS.muscle, '');
  const prescription = value.reps ?? '10';
  if (typeof prescription !== 'string' && typeof prescription !== 'number') throw new Error('Invalid workout target');
  const raw = String(prescription);
  if (raw.length > 100) throw new Error('Invalid workout target');
  const firstNumber = raw.match(/^\s*(\d+)/);
  if (!firstNumber) throw new Error('Invalid workout target');
  const amount = number(Number(firstNumber[1]), 10, LIMITS.reps, true, 1);
  const count = number(value.sets, 3, LIMITS.sets, true, 1), setTypes = readWorkoutSetTypes(value.setTypes, count);
  return { name, muscle, amount, timed: /\d\s*(?:s|sec|seconds?)\b/i.test(raw), ...copyFitnessExerciseMetadata(value),
    sets: count, ...(setTypes ? { setTypes } : {}), weight: number(value.weight, 0, LIMITS.weight),
    restSeconds: number(value.rest, 60, LIMITS.rest_seconds),
    gifUrl: value.gifUrl == null || value.gifUrl === '' ? '' : text(value.gifUrl, LIMITS.text),
  };
}

/** Backend JSON receives the same finite allocation limits as durable completion. */
export function readWorkoutPlanDays(value: unknown): WorkoutPlanDayInput[] {
  if (value == null) return [];
  return rows(value, LIMITS.exercises).map(day => {
    if (!record(day)) throw new Error('Invalid workout day');
    const exercises = rows(day.exercises ?? [], LIMITS.exercises).map(planExercise);
    buildWorkoutSetSchedule(exercises.map(exercise => ({ ...exercise, sets: Array.from({ length: exercise.sets }) })));
    return { name: text(day.name, LIMITS.name, ''), exercises };
  });
}

export function preparePlannedWorkoutExercise(value: WorkoutPlanExerciseInput, lastWeight?: number): FitnessWorkoutExercise {
  return materialize(value.name, value.muscle, lastWeight ?? value.weight, value.amount, value.sets, value.timed, value.gifUrl, value.restSeconds, copyFitnessExerciseMetadata(value as unknown as Record<string, unknown>), readWorkoutSetTypes(value.setTypes, value.sets));
}

/** Historical sets are JSON; a corrupt last weight is not a safe carry-over. */
export function readLastWorkoutWeight(value: unknown): number | null {
  if (!Array.isArray(value) || !value.length) return null;
  const last = value[value.length - 1];
  if (!record(last) || typeof last.weight !== 'number' && typeof last.weight !== 'string' || typeof last.weight === 'string' && !last.weight.trim()) return null;
  const weight = Number(last.weight);
  return Number.isFinite(weight) && weight > 0 && weight <= LIMITS.weight ? weight : null;
}
